import { Footer } from '@/app/components/Footer'
import EXTERNAL_SEARCH_CONFIG from '@/app/config/search'
import { useCourses } from '@/app/hooks/data-hooks/useCourses'
import { MAX_COURSE_FILTER_PRICE } from '@/app/lib/constants'
import { SearchInputProvider } from '@/app/modules/course/contexts/InputSearchContext'
import { Header } from '@/app/modules/shared/components/Header'
import { Newsletter } from '@/app/modules/shared/components/Newsletter'
import { TopProvidersSection } from '@/app/modules/shared/components/TopProvidersSection'
import { ContractType } from '@/types/agreement'
import { useTranslations } from 'next-intl'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { CourseFiltersModal, CoursesHeader, CoursesList } from '../components'
import { NoCoursesMessage } from '../components/NoCoursesMessage'

export const CoursesPage = () => {
  const router = useRouter()
  const t = useTranslations('Courses')
  const [term, setTerm] = useState('')

  const [isCourseFiltersModalOpen, setCourseFiltersModalOpen] = useState(false)

  const {
    data: { pages: pages = [] } = {},
    fetchNextPage: loadMore,
    hasNextPage: canLoadMore,
    isLoading: isLoadingAllCourses,
    isRefetching: isRefetchingAllCourses,
  } = useCourses(
    {},
    { limit: 6 },
    { qualityScore: 'desc', random: 'asc' },
    { retry: false }
  )

  const filteredCourses = pages.map(({ data }) => data).flat()

  const totalCourses = pages[0]?.metadata.total ?? 0
  const coursesSectionTitle = t('all_courses_in_catalog')

  const clearFilters = () => {
    router.push(router.pathname)
  }

  const redirectToSearchPage = async (data: any) => {
    const mergedData = { ...data }
    if (term) {
      mergedData.term = term
    }

    const query = Object.entries(mergedData).reduce((acc, [key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        return { ...acc, [key]: value.map((v) => v.id) }
      }

      if (value?.id) {
        return { ...acc, [key]: value.id }
      }

      if (key === 'budget') {
        return {
          ...acc,
          ...(value.min && { minPrice: value.min }),
          ...(value.max &&
            value.max !== MAX_COURSE_FILTER_PRICE && {
              maxPrice: value.max,
            }),
        }
      }

      if (
        key === 'agreementType' &&
        Object.values(ContractType).includes(value)
      ) {
        return {
          ...acc,
          [key]: value,
        }
      }

      if (typeof value === 'string' && value.trim()) {
        return { ...acc, [key]: value.trim() }
      }

      return acc
    }, {})
    await router.push({ pathname: '/search', query })
  }

  const onSearchHandler = async (term: string) => {
    if (EXTERNAL_SEARCH_CONFIG.enabled) {
      await redirectToSearchPage({ 'courses[query]': term })
    } else {
      await redirectToSearchPage({ term })
    }
  }

  return (
    <>
      <Head>
        <title>{t('title')}</title>
      </Head>
      <SearchInputProvider>
        <Header className="z-40" />
        <main>
          <div className="flex flex-col-reverse sm:flex-col">
            <CoursesHeader
              onFilter={(term) => {
                setCourseFiltersModalOpen(true)
                setTerm(term)
              }}
              className="md:mx-8"
              onSearch={async (searchTerm) => {
                await onSearchHandler(searchTerm)
              }}
            />
          </div>
          <div className="my-6 flex flex-col gap-4 sm:my-12 sm:gap-12">
            {totalCourses === 0 && !isLoadingAllCourses && (
              <NoCoursesMessage onClearFiltersClick={clearFilters} />
            )}
            <CoursesList
              title={coursesSectionTitle}
              courses={filteredCourses}
              loadMore={canLoadMore ? loadMore : undefined}
              className="container"
              isLoading={isLoadingAllCourses || isRefetchingAllCourses}
            />
          </div>
          <TopProvidersSection variant="static" />
        </main>
        <Newsletter />
        <Footer />
        <CourseFiltersModal
          hideOnClear
          open={isCourseFiltersModalOpen}
          onClose={() => setCourseFiltersModalOpen(false)}
          onSubmit={redirectToSearchPage}
        />
      </SearchInputProvider>
    </>
  )
}
